// src/components/Sections/ServicesPreview.jsx
import React, { useEffect, useMemo } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import {
  FaCube,
  FaPaintBrush,
  FaLightbulb,
  FaCogs,
  FaPlay,
  FaPhotoVideo,
  FaImage,
  FaAd,
  FaRobot,
  FaDesktop,
} from "react-icons/fa";
import SEO from "../SEO"; // Import SEO component
import "./Services.css";

// Memoized Services List → NO rerender
const servicesList = [
  {
    icon: <FaCube />,
    title: "Modeling",
    subtitle: "Turning Ideas into Reality",
    image: "/images/seo-modeling.webp",
    description: "Professional 3D modeling services to bring your ideas to life."
  },
  {
    icon: <FaPaintBrush />,
    title: "Texture",
    subtitle: "Bring Surfaces to Life",
    image: "/images/seo-texture.webp",
    description: "Expert texturing to make your 3D models look realistic."
  },
  {
    icon: <FaLightbulb />,
    title: "Lighting",
    subtitle: "Set the Mood, Tell the Story",
    image: "/images/seo-lighting.webp",
    description: "Lighting solutions to enhance mood and storytelling in visuals."
  },
  {
    icon: <FaCogs />,
    title: "Rigging",
    subtitle: "Bring Motion to Your Models",
    image: "/images/seo-rigging.webp",
    description: "Rigging for smooth character and asset animation."
  },
  {
    icon: <FaPlay />,
    title: "Animation",
    subtitle: "Make Your World Move",
    image: "/images/seo-animation.webp",
    description: "High-quality animations for films, games, and projects."
  },
  {
    icon: <FaPhotoVideo />,
    title: "Rendering",
    subtitle: "Picture-Perfect Results",
    image: "/images/seo-rendering.webp",
    description: "Photorealistic rendering for professional-grade visuals."
  },
  {
    icon: <FaImage />,
    title: "Poster & Banner",
    subtitle: "Eye-Catching Visuals That Stand Out",
    image: "/images/seo-poster.webp",
    description: "Creative poster and banner designs for branding and marketing."
  },
  {
    icon: <FaAd />,
    title: "CGI Ads",
    subtitle: "Make Your Brand Unforgettable",
    image: "/images/seo-cgiads.webp",
    description: "Engaging CGI ads to make your brand unforgettable."
  },
  {
    icon: <FaRobot />,
    title: "AI Design",
    subtitle: "Smarter, Faster, Creative",
    image: "/images/seo-ai.webp",
    description: "AI-assisted design for fast and creative outputs."
  },
  {
    icon: <FaDesktop />,
    title: "UI & UX Design",
    subtitle: "Design Better Digital Experiences",
    image: "/images/seo-uiux.webp",
    description: "User-centric UI & UX design for seamless web and mobile applications."
  }
];

function ServicesPreview() {
  const services = useMemo(() => servicesList, []);
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  const sectionVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { delay: i * 0.07, duration: 0.45, ease: "easeOut" },
    }),
  };

 // Use first service for default SEO
  const firstService = useMemo(() => servicesList[0], []);

  return (
    <>
      {/* 🔥 SEO Integration */}
      <SEO
        title="Ankielabs Services — 3D Production, VFX & Creative Studio"
        description={firstService?.description}
        image={`https://ankielabs.com${firstService?.image}`}
        url={typeof window !== "undefined" ? window.location.href : "https://ankielabs.com/services"}
        schemaType="Service"
        schemaData={{
          serviceType: firstService?.title,
          description: firstService?.description,
          image: `https://ankielabs.com${firstService?.image}`
        }}
      />

      <motion.section
        id="services"
        className="services-section"
        variants={sectionVariants}
        initial="hidden"
        animate={controls}
        ref={ref}
        style={{ willChange: "transform" }}
      >
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Our <span>Services</span>
        </motion.h2>

        <div className="services-grid">
          {services.map((service, i) => (
            <Link
              key={service.title}
              to={`/details/services?selected=${encodeURIComponent(service.title)}`}
              style={{ textDecoration: "none" }}
            >
              <motion.div
                className="service-card"
                variants={cardVariants}
                initial="hidden"
                animate={controls}
                custom={i}
                whileHover={{
                  scale: 1.08,
                  boxShadow: "0 0 25px rgba(0,203,225,0.8)",
                  transition: { duration: 0.25 },
                }}
                style={{ willChange: "transform" }}
              >
                <div className="icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.subtitle}</p>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.section>
    </>
  );
}

export default React.memo(ServicesPreview);
